import {
    BrowserStorageType,
    FRONT_GENERAL_CONFIG,
} from "@front-shared/config/general.config"

import { UtilHelper } from "./util.helper"

/**
 * Class for working with localStorage and sessionStorage inside application
 */
export class BrowserStorageHelper {
    /**
     * Set value in local storage
     */
    static set(
        key: string,
        data: any,
        browserStorageType: BrowserStorageType = BrowserStorageType.localStorage,
    ) {
        try {
            window[browserStorageType].setItem(
                BrowserStorageHelper.buildKey(key),
                UtilHelper.isObject(data) ? JSON.stringify(data) : data,
            )
        } catch (e) {}
    }

    /**
     * Get value from local storage
     */
    static get<ReturnType = any>(
        key: string,
        defaultValue?: ReturnType | null,
        browserStorageType: BrowserStorageType = BrowserStorageType.localStorage,
    ): ReturnType | null {
        let item: any = null
        defaultValue = typeof defaultValue === "undefined" ? null : defaultValue

        try {
            const storageKey = BrowserStorageHelper.buildKey(key)

            item = window[browserStorageType].getItem(storageKey) as any

            // TODO remove this in the future when we are sure all users have migrated to scoped keys
            // try to migrate unscoped value into the selected-practice key on first read
            if (item === null) {
                item = BrowserStorageHelper.migratePracticeUnscopedKey(
                    key,
                    browserStorageType,
                )
            }

            if (item === null) {
                return defaultValue
            }

            return JSON.parse(item)
        } catch (e) {
            return item === null ? defaultValue : item
        }
    }

    /**
     * Remove value from local storage
     */
    static remove(
        key: string,
        browserStorageType: BrowserStorageType = BrowserStorageType.localStorage,
    ) {
        // TODO remove this in the future when we are sure all users have migrated to scoped keys
        // in the future let buildKey handle this automatically only (without accepting scopeBySelectedPractice argument) and remove the need for this array
        // Make sure to remove values from both practice scoped and non-practice scoped keys to keep it clean for now
        const keysToRemove = [
            ...new Set([
                BrowserStorageHelper.buildKey(key),
                BrowserStorageHelper.buildKey(key, false),
            ]),
        ]

        keysToRemove.forEach(storageKey => {
            try {
                window[browserStorageType].removeItem(storageKey)
            } catch (e) {}
        })
    }

    /**
     * Update value in local storage
     */
    static update(
        key: string,
        data: any,
        browserStorageType: BrowserStorageType = BrowserStorageType.localStorage,
    ) {
        BrowserStorageHelper.remove(key, browserStorageType)
        BrowserStorageHelper.set(key, data, browserStorageType)
    }

    /**
     * Format local storage key based on config
     */
    private static buildKey(
        key: string,
        scopeBySelectedPractice?: boolean,
    ): string {
        scopeBySelectedPractice =
            typeof scopeBySelectedPractice !== "undefined"
                ? scopeBySelectedPractice
                : BrowserStorageHelper.isPracticeScopedKey(key)

        // If the key should be scoped by selected practice, we will add the practiceId to the key
        if (scopeBySelectedPractice) {
            const practiceId = BrowserStorageHelper.get<number>(
                FRONT_GENERAL_CONFIG.browserStorageKeys.selectedPracticeId,
                undefined,
                BrowserStorageType.localStorage,
            )

            // Expectation is that practiceId exists in this scenario
            // But if it doesn't, we will fall back to the unscoped key rather than error
            if (typeof practiceId === "number") {
                return `nirvana-${practiceId}-${key}`
            }
        }

        return `nirvana-${key}`
    }

    /**
     * Check if a key is practice scoped based on config
     */
    private static isPracticeScopedKey(key: string): boolean {
        return FRONT_GENERAL_CONFIG.browserStorageKeysPracticeScoped.some(
            partialKey => key.includes(partialKey),
        )
    }

    /**
     * Move an unscoped value into the selected-practice key on first scoped read
     * TODO remove this in the future when we are sure all users have migrated to scoped keys
     */
    private static migratePracticeUnscopedKey(
        key: string,
        browserStorageType: BrowserStorageType,
    ): string | null {
        if (!BrowserStorageHelper.isPracticeScopedKey(key)) {
            return null
        }

        const unscopedStorageKey = BrowserStorageHelper.buildKey(key, false)
        const scopedStorageKey = BrowserStorageHelper.buildKey(key, true)

        // Should never happen, just in case
        if (scopedStorageKey === unscopedStorageKey) {
            return null
        }

        const legacyItem =
            window[browserStorageType].getItem(unscopedStorageKey)

        if (legacyItem === null) {
            return null
        }

        window[browserStorageType].setItem(scopedStorageKey, legacyItem)
        window[browserStorageType].removeItem(unscopedStorageKey)

        return legacyItem
    }
}
